/**
 * Tiny leveled logger for the Fineco advisor SPA.
 *
 * Troubleshooting-first goal: from the browser console alone an engineer should
 * be able to read the INTENT of a meaningful operation ("submitting constraints
 * (rebalancing)", "importing portfolio to X-Net") and its RESULT (ok / failed +
 * status), correlated by the ids that flow through the flow (threadId,
 * portfolioId, and any backend request id) — WITHOUT the render-trace and
 * payload-dump noise that used to drown the console.
 *
 * Levels
 *   debug — developer tracing (per-render / per-keystroke / intermediate state).
 *           Silenced in production builds; on in development.
 *   info  — intent + result of meaningful operations. Always emitted.
 *   warn  — recoverable / unexpected-but-handled conditions. Always emitted.
 *   error — a failed operation, with context. Always emitted.
 *
 * NEVER pass secrets or PII: no JWTs / session tokens, no full customer or
 * portfolio payloads. Log ids, statuses, modes and short summaries only. The
 * optional `context` object is meant for ids and an `error` — keep it small.
 */

const isProduction = process.env.NODE_ENV === 'production';

export type LogContext = Record<string, unknown>;

type Level = 'debug' | 'info' | 'warn' | 'error';

function emit(level: Level, scope: string, message: string, context?: LogContext): void {
  const line = `[${scope}] ${message}`;
  // eslint-disable-next-line no-console
  const sink = console[level] ?? console.log;
  if (context && Object.keys(context).length > 0) {
    sink(line, context);
  } else {
    sink(line);
  }
}

export interface Logger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, context?: LogContext): void;
}

/**
 * Create a scoped logger. The scope is prefixed to every line so operations can
 * be grepped/filtered by subsystem (e.g. `[JwtAuthService]`, `[FinecoPortfolio]`).
 */
export function createLogger(scope: string): Logger {
  return {
    debug: (message, context) => {
      if (isProduction) return;
      emit('debug', scope, message, context);
    },
    info: (message, context) => emit('info', scope, message, context),
    warn: (message, context) => emit('warn', scope, message, context),
    error: (message, context) => emit('error', scope, message, context),
  };
}

/** Default un-scoped logger for one-off call sites. */
export const logger = createLogger('app');

export default logger;
